import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Sensor } from '../../../../model/sensor';
import { FileInfo } from '../../../../model/attachment';
import { Subject, takeUntil } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AttachmentQualificatesService } from '../../../../services/attachmentQualificates.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxImageCompressService } from 'ngx-image-compress';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackBarService } from '../../../../services/snackBar.service';
import { SpinnerService } from '../../../../services/spinner.service';
import { saveAs } from 'file-saver';
import { PhotoUtils } from '../../../../utils/photoUtils';
import { ExcelUtils } from '../../../../utils/excelUtils';
import { SensorData } from '../../../../model/sensorData';

export interface DialogDataAttachmentDetails {
  idExecutionTest: number;
  idSensor: number;
  idAttachment: number;
  accept: string;
  isPhoto: boolean;
  isEssay: boolean;
  disableUpload: boolean;
  type: string;
  sensor: Sensor;
  idVariable: number;
}

@Component({
    selector: 'app-execution-q-edit-attachment-details',
    templateUrl: './execution-q-edit-attachment-details.component.html',
    styleUrl: './execution-q-edit-attachment-details.component.scss',
    standalone: false,
})
export class ExecutionQEditAttachmentDetailsComponent implements OnInit, OnDestroy {

  fileData: FileInfo;
  image: any;
  base64: string;

  canRotate: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    private sanitizer: DomSanitizer,
    private attachmentQualificatesService: AttachmentQualificatesService,
    private translate: TranslateService,
    private imageCompress: NgxImageCompressService,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataAttachmentDetails,
    public dialogRef: MatDialogRef<ExecutionQEditAttachmentDetailsComponent>,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService) { }

  ngOnInit(): void {
    this.reloadAttachment();

    this.canRotate = !this.data.isEssay;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadAttachment(): void {
    this.spinnerService.show();

    this.fileData = new FileInfo();

    if (this.data.isEssay) {
      this.attachmentQualificatesService.getAttachmentInfoToEssay(this.data.idExecutionTest, this.data.idSensor)
        .pipe(takeUntil(this.destroy$)).subscribe((res: FileInfo) => this.fileData = res);
    } else {
      this.attachmentQualificatesService.getAttachmentInfoToSensor(this.data.idSensor, this.data.idAttachment).pipe(takeUntil(this.destroy$))
        .subscribe((res: FileInfo) => this.fileData = res);
    }


    if (this.data.isPhoto) {
      if (this.data.isEssay) {
        this.attachmentQualificatesService.downloadPhotoToEssay(this.data.idExecutionTest, this.data.idSensor)
          .pipe(takeUntil(this.destroy$)).subscribe((res: Blob) => this.printImage(res), () => {
            this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.downloadImage.error.generic') as string);

            this.spinnerService.hide();
          });
      } else {
        this.attachmentQualificatesService.downloadAttachmentToSensor(this.data.idSensor, this.data.idAttachment).pipe(takeUntil(this.destroy$))
          .subscribe((res: Blob) => this.printImage(res), () => {
            this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.downloadImage.error.generic') as string);

            this.spinnerService.hide();
          });
      }
    } else {
      this.spinnerService.hide();
    }
  }

  downloadAttachment(): void {
   this.spinnerService.show();
   if (this.data.isEssay) {
    this.attachmentQualificatesService.downloadPhotoToEssay(this.data.idExecutionTest, this.data.idSensor).pipe(takeUntil(this.destroy$))
        .subscribe((res: Blob) => {
          saveAs(res, this.fileData.filename);
          this.spinnerService.hide();
        }, error => {
          console.error(error);
          this.spinnerService.hide();
        });
   } else {
     this.attachmentQualificatesService.downloadAttachmentToSensor(this.data.idSensor, this.data.idAttachment).pipe(takeUntil(this.destroy$))
      .subscribe((res: Blob) => {
        saveAs(res, this.fileData.filename);
        this.spinnerService.hide();
      }, error => {
        console.error(error);
        this.spinnerService.hide();
      });
   }
  }

  deleteAttachment(): void {
    this.spinnerService.show();

    this.attachmentQualificatesService.deleteAttachmentToSensor(this.data.idSensor, this.data.idAttachment).pipe(takeUntil(this.destroy$))
      .subscribe(() => {

        this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.deleteGeneric.ok') as string);
        this.spinnerService.hide();
      }, error => {
        console.error(error);
        this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.deleteGeneric.error.generic') as string);
        this.spinnerService.hide();
      });
  }

  uploadFile(event): void {

    const file = event.target.files[0] as File;

    if (file == null) {
      return;
    }

    this.spinnerService.show();

    if (this.data.type === 'photo') {
      PhotoUtils.fixImage(file, this.imageCompress, (res: File) => {

        this.attachmentQualificatesService.uploadPhotoToSensor(this.data.idSensor, res).pipe(takeUntil(this.destroy$))
          .subscribe((item: number) => {
            this.spinnerService.hide();

            this.data.sensor.idPhoto = item;
            this.data.idAttachment = item;
            this.reloadAttachment();

            this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadGeneric.ok') as string);
          }, () => {
            this.spinnerService.hide();

            this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadGeneric.error.generic') as string);
          });

      });
    } else if (this.data.type === 'primary') {
      this.attachmentQualificatesService.uploadPrimaryFileToSensor(this.data.idSensor, file).pipe(takeUntil(this.destroy$))
        .subscribe((item: number) => {
          this.spinnerService.hide();

          this.data.sensor.idPrimaryDataFile = item;
          this.data.idAttachment = item;
          this.reloadAttachment();

          this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadGeneric.ok') as string);
        }, () => {
          this.spinnerService.hide();

          this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadGeneric.error.generic') as string);
        });
    } else if (this.data.type === 'excel') {

      ExcelUtils.excelToSensorDataQualificates(file, this.attachmentQualificatesService, this.data.idVariable, false, 1).then((data: SensorData[]) => {

        data.forEach(item => {
          item.serialNum = this.data.sensor.serialNum;
          item.equipmentName = this.data.sensor.equipmentName;
          item.idEquipment = this.data.sensor.idEquipment;
        });

        data.forEach(item => delete item.posEquipment);

        this.data.sensor.data = data;

        data = data.filter(item => item.date != null && !isNaN(item.date.getTime()));

        this.attachmentQualificatesService.uploadExcelToSensor(this.data.idSensor, file, data).pipe(takeUntil(this.destroy$))
          .subscribe((item: number) => {

            this.data.sensor.idExcel = item;
            this.data.idAttachment = item;
            this.reloadAttachment();

            this.spinnerService.hide();

            this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadExcel.ok') as string);
          }, () => {
            this.spinnerService.hide();

            this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadExcel.error.generic') as string);
          }
          );

      }).catch(err => {
        this.snackBarService.sendError(err as string);
        this.spinnerService.hide();
      });
    } else if (this.data.type === 'generalPhoto') {
      PhotoUtils.fixImage(file, this.imageCompress, (res: File) => {

        this.attachmentQualificatesService.uploadGeneralPhoto(this.data.idExecutionTest, this.data.idSensor, res).pipe(takeUntil(this.destroy$))
          .subscribe((item: number) => {
            this.spinnerService.hide();

            this.data.idAttachment = item;
            this.reloadAttachment();

            this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadGeneric.ok') as string);
          }, () => {
            this.spinnerService.hide();

            this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadGeneric.error.generic') as string);
          });

      });
    } else {
      this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadGeneric.error.generic') as string);
      this.spinnerService.hide();
    }
  }

  base64ToImage(): SafeResourceUrl {
    if (this.base64 == null) {
      return '';
    }

    if (!this.base64.startsWith('data:image')) {
      this.base64 = 'data:image/jpg;base64,' + this.base64;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(this.base64);
  }

  rotateImageLeft(): void {
    this.spinnerService.show();

    this.attachmentQualificatesService.rotateSensorImageLeft(this.data.idSensor).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.spinnerService.hide();
      this.reloadAttachment();
    });
  }

  rotateImageRight(): void {
    this.spinnerService.show();

    this.attachmentQualificatesService.rotateSensorImageRight(this.data.idSensor).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.spinnerService.hide();
      this.reloadAttachment();
    });
  }

  onNoClick(): void {
    this.dialogRef.close(this.data.sensor);
  }

  private printImage(res: Blob): void {
    this.image = res;

    const cthis = this;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;

      let base64: any;
      if (typeof dataUrl !== 'string') {
        base64 = dataUrl;
      } else {
        base64 = dataUrl.split(',')[1];
      }

      cthis.base64 = base64;

      cthis.spinnerService.hide();
    };

    reader.readAsDataURL(this.image);
  }
}
